import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import { IParsedWhitelabel } from '@/Interfaces/Whitelabels/Whitelabel.interface';
import CurrentWhitelabelService from '@/Services/Whitelabels/CurrentWhitelabel.service';
import { WHITELABELS_ENDPOINT } from '@/constants/endpoints';
import type { SearchWhitelabelsPayload } from '@/types/whitelabels.types';
import axios from 'axios';

export const fetchWhitelabels = async (
  params?: SearchWhitelabelsPayload,
  options?: { signal?: AbortSignal },
): Promise<IPaginatedApiResponse<any[]>> => {
  const response = await axios.get(WHITELABELS_ENDPOINT, {
    params: { ...params, limit: 1000 },
    signal: options?.signal,
  });
  return response.data;
};

export const rerunVerification = async (whitelabelId: IParsedWhitelabel['id']): Promise<void> => {
  return await axios.post(`/whitelabels/${whitelabelId}/verify`);
};

/**
 * Prefer the authenticated user's own whitelabel over the one the URL implies.
 *
 * `/whitelabels/current` is unauthenticated and resolves by host, falling back to
 * the default whitelabel when the host matches none. So on any URL that is not a
 * whitelabel's own domain — every preview environment, and app.ribeye.media for a
 * client who has their own — the app was branded and feature-flagged as another
 * tenant while the API acted as the user's real one. That is how a rate-card
 * client came to see the Bid CPM field (ribeye-api#2332).
 *
 * `/whitelabels/mine` returns the same payload for the authenticated user, and is
 * membership-checked server-side. Signed out there is no user to ask, so the
 * host-based response is still the right answer for login-screen branding.
 */
export const getCurrentWhitelabelData = async () => {
  try {
    const response = await axios.get(`${WHITELABELS_ENDPOINT}/mine`);
    return CurrentWhitelabelService.formatCurrentWhitelabelResponse(response.data);
  } catch {
    // Signed out, or an older API without the endpoint — fall back to host-based.
  }

  try {
    const response = await axios.get(`${WHITELABELS_ENDPOINT}/current`);
    return CurrentWhitelabelService.formatCurrentWhitelabelResponse(response.data);
  } catch {
    return CurrentWhitelabelService.defaultCurrentWhitelabel;
  }
};
